import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { EventItem } from "@/types";
import { formatDate, getCategoryName } from "@/utils/data";

export default function HeroCarousel({ slides }: { slides: EventItem[] }) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => setActive((v) => (v + 1) % slides.length), 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (!slides.length) return null;
  const current = slides[active];

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        {slides.map((s, i) => (
          <img
            key={s.id}
            src={s.cover}
            alt={s.title}
            title={`${s.title} 焦点赛事`}
            className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/75 to-primary-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>
      </div>

      <div className="relative mx-auto w-full px-4 py-10 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.6fr_1fr]">
          <div className="flex flex-col">
            <span className="flex w-fit items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              <i className="ri-flashlight-fill"></i>
              今日焦点 · 第 {active + 1}/{slides.length} 条
            </span>
            <h1 className="mt-4 text-2xl font-extrabold leading-tight tracking-tight text-background-50 md:text-4xl">
              每日赛事资讯 · 今日黑榜 mdrs 排行
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              {current.summary}
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2 text-xs text-background-200">
              <span className="flex items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-1">
                <i className="ri-price-tag-3-line text-accent-400"></i>
                {getCategoryName(current.categoryId)}
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-1">
                <i className="ri-calendar-line text-accent-400"></i>
                {formatDate(current.date)} {current.time}
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-1">
                <i className="ri-map-pin-line text-accent-400"></i>
                {current.location}
              </span>
            </div>

            <h2 className="mt-6 text-lg font-bold text-background-50 md:text-2xl">{current.title}</h2>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-article-line"></i>
                阅读赛事实况
              </Link>
              <Link
                to="/blacklist"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                <i className="ri-fire-line"></i>
                查看今日黑榜
              </Link>
            </div>
          </div>

          <div className="hidden flex-col gap-3 lg:flex">
            <p className="text-xs font-semibold uppercase tracking-widest text-background-300">焦点赛事速览</p>
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(i)}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 text-left transition-colors ${
                  i === active
                    ? "border-accent-500 bg-background-50/10"
                    : "border-background-50/15 hover:bg-background-50/5"
                }`}
              >
                <span className="h-12 w-16 shrink-0 overflow-hidden rounded-md bg-primary-900">
                  <img src={s.cover} alt={s.title} className="h-full w-full object-cover object-top" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-background-50">{s.title}</span>
                  <span className="mt-0.5 block text-xs text-background-300">
                    {getCategoryName(s.categoryId)} · {formatDate(s.date)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center gap-2">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 条焦点`}
              onClick={() => setActive(i)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                i === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/40"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}